"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import { gsap } from "gsap";
import { crossfadeBackground } from "@/lib/animations";
import type { Collection } from "@/types";

const FALLBACKS = [
  "linear-gradient(160deg,#4a3320,#1a1410)",
  "linear-gradient(160deg,#3a2c1c,#181310)",
  "linear-gradient(160deg,#4a3a22,#201810)",
  "linear-gradient(160deg,#382e22,#151210)",
];

interface HeroBackgroundProps {
  collections: Collection[];
  activeIndex: number;
  prevIndex: number;
  duration: number;
}

export function HeroBackground({
  collections,
  activeIndex,
  prevIndex,
  duration,
}: HeroBackgroundProps) {
  const layerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lastActive = useRef(activeIndex);

  // Captured ONCE on mount and never updated again — this is what the
  // very first render's inline style is based on. Because it's frozen,
  // no later re-render can ever overwrite the DOM's opacity via JSX.
  // After mount, GSAP is the ONLY thing allowed to touch opacity.
  const initialActiveRef = useRef(activeIndex);

  // Crossfade whenever the active slide changes — runs via GSAP only,
  // React never re-touches these elements' opacity after first paint.
  useEffect(() => {
    if (lastActive.current === activeIndex) return;
    lastActive.current = activeIndex;

    const incoming = layerRefs.current[activeIndex];
    const outgoing = layerRefs.current[prevIndex];
    if (!incoming) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    crossfadeBackground({
      incoming,
      outgoing: outgoing ?? null,
      duration,
      reduced,
    });
  }, [activeIndex, prevIndex, duration]);

  return (
    <div
      style={{ position: "absolute", inset: 0, overflow: "hidden" }}
      aria-hidden="true"
    >
      {collections.map((collection, i) => {
        const distance = Math.abs(i - activeIndex);
        // Only ever mount the active slide + its immediate neighbours.
        // This is what fixes the refresh feeling "stuck" — instead of
        // every slide's image competing for bandwidth/decode time on
        // first paint, only 3 images ever load at once.
        const shouldLoad = distance <= 1;

        return (
          <div
            key={collection.id}
            ref={(el) => {
              layerRefs.current[i] = el;
            }}
            style={{
              position: "absolute",
              inset: 0,
              background: FALLBACKS[i % FALLBACKS.length],
              // Set ONCE from the frozen initial value. Never derived
              // from `activeIndex` again after this — that's the fix.
              opacity: i === initialActiveRef.current ? 1 : 0,
              willChange: "opacity, transform",
            }}
          >
            {shouldLoad && (
              <Image
                src={collection.backgroundImage}
                alt=""
                fill
                priority={i === initialActiveRef.current}
                sizes="100vw"
                quality={75}
                className="object-cover"
                draggable={false}
              />
            )}
          </div>
        );
      })}

      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 10,
          pointerEvents: "none",
          background: [
            "linear-gradient(90deg,rgba(6,5,4,0.92) 0%,rgba(6,5,4,0.58) 42%,rgba(6,5,4,0.28) 68%,rgba(6,5,4,0.52) 100%)",
            "linear-gradient(0deg,rgba(6,5,4,0.85) 0%,rgba(6,5,4,0) 35%)",
          ].join(", "),
        }}
      />
    </div>
  );
}